import { makeObservable, observable, runInAction } from 'mobx';
import { nanoid } from 'nanoid';
import { mapCustomerAttributeTypeToSaveType } from '@common/customerAttributes/mapCustomerAttributeTypeToSaveType';
import { CustomerAttribute, CustomerAttributeSave } from '@common/customerAttributes/types';
import type { MfeLogPayload } from '@common/utils/getMfeLogPayload';
import { Tracker } from '@src/classes/Tracker';
import {
  Identity,
  ErrorResponse,
  PaginatedHistory,
} from '@src/types/comsApi';
import { ApiLoadConsentResponse, TefComs } from '@src/types/typings';
import { HttpClient } from '@src/util/HttpClient';
import { Service } from '@src/util/Service';

type ConsentAndCustomerAttributeSaveType = TefComs.ConsentStatus | CustomerAttributeSave;

export class ClientStore {
  public processing = false;
  public error?: Error = undefined;

  private config: TefComs.ConfigOptions;
  private clienttraceid: string;
  private httpClient: HttpClient;
  private identity?: Identity;

  private service;
  private tracker;

  public constructor(config: TefComs.ConfigOptions) {
    makeObservable(this, {
      error: observable,
      processing: observable,
    });
    this.config = config;
    this.service = new Service();
    this.tracker = new Tracker();
    const {
      apiUri, agentToken, gatewayToken, traceid, fetch: altfetch,
    } = config;
    this.clienttraceid = traceid || nanoid();
    this.httpClient = new HttpClient(apiUri, {
      ...(agentToken ? { 'x-authorization-override': `Agent ${agentToken}` } : {}),
      ...(gatewayToken ? { Authorization: `Bearer ${gatewayToken}` } : {}),
    }, altfetch);
  }

  private $getTracingHeaders(): Record<string, string> {
    const requesttraceid = nanoid(); // a new one for each single request
    return {
      'tef-coms-clienttraceid': this.clienttraceid,
      'tef-coms-traceid': requesttraceid,
      activityid: `${this.clienttraceid}|${requesttraceid}`, // for api gateway, ignored by COMS
    };
  }

  private isRaitt(): boolean {
    return !!this.config.spId;
  }

  private getSpId(): string {
    return (this.isRaitt() ? this.config.spId : this.identity?.spId) as string;
  }

  private getLcId(): string | undefined {
    return this.isRaitt() ? this.config.lcId : this.identity?.lcId;
  }

  private getRegisterId(): string | undefined {
    return this.isRaitt() ? this.config.registerId : this.identity?.registerId;
  }

  private async $processPromise<T>(
    func: () => Promise<T>,
    eventNameSuccess: string,
    eventNameError: string,
    eventMessageSuccess: string,
    eventMessageError: string,
  ) {
    runInAction(() => {
      this.processing = true;
      this.error = undefined;
    });
    try {
      const data: T = await func();
      this.service.fireEvent<TefComs.EventDetails>({
        name: eventNameSuccess,
        level: 'info',
        message: eventMessageSuccess,
      });
      return data;
    } catch (error) {
      this.service.fireEvent<TefComs.EventDetails>({
        name: eventNameError,
        level: 'error',
        message: eventMessageError,
        error: error as Error,
      });
      this.tracker.comsError(eventMessageSuccess);
      runInAction(() => {
        this.error = error as Error;
      });
      throw error;
    } finally {
      runInAction(() => {
        this.processing = false;
      });
    }
  }

  public async getIdentity(): Promise<Identity> {
    return this.$processPromise<Identity>(
      this.$getIdentity.bind(this) as () => Promise<Identity>,
      'auth/VALIDATE',
      'auth/ERROR',
      'token validation successful',
      'token validation failed',
    );
  }

  private async $getIdentity() {
    const path = this.isRaitt() ? '/identity' : '/agentinfo';
    const headers = {
      ...this.$getTracingHeaders(),
    };
    this.identity = await this.httpClient.get<Identity, ErrorResponse>(
      path,
      undefined,
      headers,
    );
    return this.identity;
  }

  private checkIdentity() {
    if (!this.identity) {
      throw new Error('identity is not set');
    }
  }

  public async getConsentHistory(
    configId: string,
    offset: number,
    limit: number,
  ): Promise<PaginatedHistory> {
    this.checkIdentity();

    return this.$processPromise<PaginatedHistory>(
      this.$getConsentHistory.bind(this, configId, offset, limit) as () => Promise<PaginatedHistory>,
      'history/LOAD',
      'history/ERROR',
      'loading history successful',
      'loading history failed',
    );
  }

  private async $getConsentHistory(
    configId: string,
    offset: number,
    limit: number,
  ): Promise<PaginatedHistory> {
    const spId = this.getSpId();
    const lcId = this.getLcId();
    const path = '/service/history';
    const query = {
      lcId,
      spId,
      configId,
      offset,
      limit,
    };
    const headers = {
      ...this.$getTracingHeaders(),
    };
    return this.httpClient.get<PaginatedHistory, ErrorResponse>(
      path,
      query,
      headers,
    );
  }

  public async loadConsents(): Promise<ApiLoadConsentResponse> {
    this.checkIdentity();
    const { isExistingRegister } = this.config;
    // bugfix #247: if isExistingRegister is set GET register should be called in any case
    const lcId = this.getLcId();
    return this.$processPromise(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      lcId && !isExistingRegister
        ? this.$getConsentCustomer.bind(this)
        : this.$getConsentRegister.bind(this),
      'consents/LOAD',
      'consents/ERROR',
      'loading consents successful',
      'loading consents failed',
    );
  }

  private async $getConsentCustomer(): Promise<ApiLoadConsentResponse> {
    const spId = this.getSpId();
    const lcId = this.getLcId();
    const { lifecycle, consentwhitelist } = this.config;
    const path = `/consent/sp/${spId}/customer/${lcId}`;
    const query = {
      lifecycle,
      textformat: 'html',
      ignoreNotFound: true,
      extended: true,
      additionalInfo: true,
      pageformat: true,
      includeCustomerAttributes: true,
    };
    const headers = {
      ...this.$getTracingHeaders(),
      ...(consentwhitelist
        ? { 'tef-coms-comment': `consentwhitelist:${consentwhitelist}` }
        : {}),
    };
    return this.httpClient.get<ApiLoadConsentResponse, ErrorResponse>(
      path,
      query,
      headers,
    );
  }

  private async $getConsentRegister(): Promise<ApiLoadConsentResponse> {
    const spId = this.getSpId();
    const registerId = this.getRegisterId();
    const { lifecycle, consentwhitelist, isExistingRegister } = this.config;
    const path = isExistingRegister
      ? `/consent/sp/${spId}/register/${registerId}`
      : `/consent/sp/${spId}/register`;
    const query = {
      lifecycle,
      textformat: 'html',
      extended: true,
      additionalInfo: true,
      pageformat: true,
      includeCustomerAttributes: true,
    };
    const headers = {
      ...this.$getTracingHeaders(),
      ...(consentwhitelist
        ? { 'tef-coms-comment': `consentwhitelist:${consentwhitelist}` }
        : {}),
    };
    return this.httpClient.get<ApiLoadConsentResponse, ErrorResponse>(
      path,
      query,
      headers,
    );
  }

  public async saveConsents(
    consents: TefComs.ConsentStatus[],
    references: TefComs.Reference[] = [],
    customerAttributes: CustomerAttribute[] = [],
  ): Promise<void> {
    this.checkIdentity();

    return this.$processPromise(
      this.$saveConsents.bind(this, consents, references, customerAttributes) as () => Promise<void>,
      'consents/SAVE',
      'consents/ERROR',
      'saving consents successful',
      'saving consents failed',
    );
  }

  private async $saveConsents(
    consents: TefComs.ConsentStatus[],
    references: TefComs.Reference[],
    customerAttributes: CustomerAttribute[],
  ): Promise<void> {
    const {
      lifecycle,
      consentwhitelist,
      channel,
      usertype,
      vonr,
      isNewCustomer,
    } = this.config;
    const spId = this.getSpId();
    const lcId = this.getLcId();
    const registerId = this.getRegisterId();
    const username = this.identity?.username ?? 'undefined';
    const path = lcId
      ? `/consent/sp/${spId}/customer/${lcId}`
      : `/consent/sp/${spId}/register/${registerId}`;
    const query: {
      lifecycle: string,
      registerId?: string
      isNewCustomer?: boolean
    } = { lifecycle };

    if (registerId && lcId) {
      query.registerId = registerId;
    }

    if (isNewCustomer) {
      query.isNewCustomer = true;
    }

    // Collect consents and customer attributes
    const consentsSaveData = consents.map((consent) => ({ ...consent, type: 'consent' }));
    const customerAttributesSaveData = customerAttributes.map(
      (customerAttributeData) => mapCustomerAttributeTypeToSaveType(customerAttributeData),
    );

    const consentsAndCustomerAttributes = [...consentsSaveData, ...customerAttributesSaveData];

    const headers = {
      ...this.$getTracingHeaders(),
      'tef-coms-username': vonr ? `${vonr} | ${username}` : username,
      'tef-coms-usertype': usertype || 'Agent',
      'tef-coms-channel': channel || 'Shop',
      ...(consentwhitelist
        ? { 'tef-coms-comment': `consentwhitelist:${consentwhitelist}` }
        : {}),
      ...(references.length
        ? { 'tef-coms-references': JSON.stringify(references) }
        : {}),
    };
    await this.httpClient.post<ConsentAndCustomerAttributeSaveType[], ErrorResponse>(
      path,
      consentsAndCustomerAttributes,
      query,
      headers,
    );
  }

  public async logMicrofrontendParams(mfeLogPayload: MfeLogPayload): Promise<void> {
    return this.httpClient.post('/service/mfe/log', mfeLogPayload);
  }
}
